/**
 * The three sidebar sections that are new on `/saved` (SPEC `saved-posts` § Sidebar sections).
 *
 * The other five — Post type, Content type, Performance, Attributes, Length — are the feed's
 * sections, mounted unchanged: they take value + onChange and know nothing about which screen they
 * are on, which is exactly why they were built that way.
 *
 * Lists and Tags are checkbox lists with a per-row kebab (Rename / Delete) over an inline create
 * row — the save popup's "+ New list" affordance, verbatim, because the vocabulary is one thing and
 * the two places that edit it should not look like two features. The destructive half is stated
 * plainly in the confirm, because neither action does what it looks like it does: deleting a list
 * never deletes a save, and deleting a tag only detaches it.
 */

import { useState } from 'react';
import type { SavedListDTO, SavedTagDTO } from '@lw/shared';
import { SAVED_LIST_NAME_MAX, SAVED_TAG_NAME_MAX } from '@lw/shared';
import {
  CheckboxList,
  ConfirmDialog,
  KebabIcon,
  Menu,
  MenuItem,
  PlusIcon,
  cx,
} from '../../components';
import { describeError } from '../../api/client';
import { fromDateInputValue, toDateInputValue } from '../../lib/datetime';
import {
  useCreateSavedList,
  useCreateSavedTag,
  useDeleteSavedList,
  useDeleteSavedTag,
  useRenameSavedList,
  useRenameSavedTag,
} from './useSavedCollections';

/* ------------------------------------------------------------- rename row */

type RenameTarget = { kind: 'list' | 'tag'; id: number; name: string };

/** Nothing renders until there is a target, so the draft can be seeded from it directly. */
function RenameDialog({ target, onClose }: { target: RenameTarget | null; onClose: () => void }) {
  if (target === null) return null;
  // Keyed by the target at the call site, so this remounts per target and seeds exactly once.
  return <RenameForm target={target} onClose={onClose} />;
}

function RenameForm({ target, onClose }: { target: RenameTarget; onClose: () => void }) {
  const [name, setName] = useState(target.name);
  const [error, setError] = useState<string | null>(null);
  const renameList = useRenameSavedList();
  const renameTag = useRenameSavedTag();
  const busy = renameList.isPending || renameTag.isPending;

  const submit = () => {
    const trimmed = name.trim();
    if (trimmed.length === 0 || trimmed === target.name) {
      onClose();
      return;
    }
    const options = {
      onSuccess: () => onClose(),
      onError: (failure: Error) => setError(describeError(failure)),
    };
    if (target.kind === 'list') renameList.mutate({ id: target.id, name: trimmed }, options);
    else renameTag.mutate({ id: target.id, name: trimmed }, options);
  };

  return (
    <ConfirmDialog
      open
      title={target.kind === 'list' ? 'Rename list' : 'Rename tag'}
      confirmLabel="Rename"
      busy={busy}
      onConfirm={submit}
      onCancel={onClose}
      message={
        <div className="field">
          <label className="field__label" htmlFor="saved-rename">
            Name
          </label>
          <input
            id="saved-rename"
            className="input"
            autoFocus
            value={name}
            maxLength={target.kind === 'list' ? SAVED_LIST_NAME_MAX : SAVED_TAG_NAME_MAX}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submit();
              }
            }}
          />
          {error !== null ? <span className="field__error">{error}</span> : null}
        </div>
      }
    />
  );
}

/* ------------------------------------------------------------- create row */

/**
 * "+ New list" / "+ New tag" — collapsed to a ghost button until pressed, then one field that Enter
 * commits and Escape abandons. `stopPropagation` on Escape so it cannot also close the drawer.
 *
 * The two entities are created by different endpoints (#57 / #67) but identically here, so the
 * caller passes the mutation rather than a kind discriminator.
 */
function CreateRow({
  label,
  placeholder,
  maxLength,
  busy,
  onSubmit,
}: {
  label: string;
  placeholder: string;
  maxLength: number;
  busy: boolean;
  onSubmit: (name: string, done: (error: string | null) => void) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const name = (draft ?? '').trim();
    if (name.length === 0) {
      setDraft(null);
      setError(null);
      return;
    }
    onSubmit(name, (failure) => {
      if (failure === null) {
        setDraft(null);
        setError(null);
      } else setError(failure);
    });
  };

  if (draft === null) {
    return (
      <button
        type="button"
        className="btn-ghost btn-ghost--sm saved-create"
        onClick={() => setDraft('')}
      >
        <PlusIcon size={12} />
        {label}
      </button>
    );
  }

  return (
    <>
      <div className="saved-create__row">
        <input
          className="input"
          autoFocus
          value={draft}
          maxLength={maxLength}
          placeholder={placeholder}
          aria-label={label}
          onChange={(event) => {
            setDraft(event.target.value);
            setError(null);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submit();
            }
            if (event.key === 'Escape') {
              event.stopPropagation();
              setDraft(null);
              setError(null);
            }
          }}
        />
        <button
          type="button"
          className="btn-primary btn-primary--sm"
          disabled={busy}
          onClick={submit}
        >
          Add
        </button>
      </div>
      {error === null ? null : <span className="field__error">{error}</span>}
    </>
  );
}

/* ------------------------------------------------------------------ lists */

export type ListsSectionProps = {
  lists: readonly SavedListDTO[];
  selected: readonly number[];
  onChange: (next: number[]) => void;
};

export function SavedListsSection({ lists, selected, onChange }: ListsSectionProps) {
  const [renaming, setRenaming] = useState<RenameTarget | null>(null);
  const [deleting, setDeleting] = useState<SavedListDTO | null>(null);
  const remove = useDeleteSavedList();
  const create = useCreateSavedList();

  return (
    <>
      {/* Nothing checked *is* "All Saved" — there is no system list to check, so nothing to show
          for it either. The umbrella is the absence of a selection. */}
      <CheckboxList
        options={lists.map((list) => ({
          value: list.id,
          label: list.name,
          hint: (
            <SavedRowMenu
              label={list.name}
              onRename={() => setRenaming({ kind: 'list', id: list.id, name: list.name })}
              onDelete={() => setDeleting(list)}
              count={list.postCount}
            />
          ),
        }))}
        selected={selected}
        onChange={onChange}
        emptyLabel="No lists yet."
      />

      <CreateRow
        label="New list"
        placeholder="List name"
        maxLength={SAVED_LIST_NAME_MAX}
        busy={create.isPending}
        onSubmit={(name, done) => {
          create.mutate(name, {
            onSuccess: () => done(null),
            onError: (error) => done(describeError(error)),
          });
        }}
      />

      <RenameDialog key={`${renaming?.kind ?? ''}-${String(renaming?.id ?? 0)}`} target={renaming} onClose={() => setRenaming(null)} />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete list"
        destructive
        confirmLabel="Delete list"
        busy={remove.isPending}
        message={
          <p>
            Delete <strong>{deleting?.name}</strong>? The {deleting?.postCount ?? 0} posts in it stay
            saved — only the list goes.
          </p>
        }
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting === null) return;
          remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
        }}
      />
    </>
  );
}

/* ------------------------------------------------------------------- tags */

export type TagsSectionProps = {
  tags: readonly SavedTagDTO[];
  selected: readonly number[];
  onChange: (next: number[]) => void;
};

export function SavedTagsSection({ tags, selected, onChange }: TagsSectionProps) {
  const [renaming, setRenaming] = useState<RenameTarget | null>(null);
  const [deleting, setDeleting] = useState<SavedTagDTO | null>(null);
  const remove = useDeleteSavedTag();
  const create = useCreateSavedTag();

  return (
    <>
      {/* Any-of, not all-of: two checked tags mean "either", which is what a reader looking for
          "that design or copywriting thing" actually wants. */}
      <CheckboxList
        options={tags.map((tag) => ({
          value: tag.id,
          label: tag.name,
          lead: <span className="tag-dot" data-color={tag.color} aria-hidden="true" />,
          hint: (
            <SavedRowMenu
              label={tag.name}
              onRename={() => setRenaming({ kind: 'tag', id: tag.id, name: tag.name })}
              onDelete={() => setDeleting(tag)}
              count={tag.postCount}
            />
          ),
        }))}
        selected={selected}
        onChange={onChange}
        emptyLabel="No tags yet."
      />

      {/* A tag created here starts empty (`postCount: 0`) and stays in the vocabulary until it is
          deleted — the same lifetime a tag detached from its last post has. */}
      <CreateRow
        label="New tag"
        placeholder="Tag name"
        maxLength={SAVED_TAG_NAME_MAX}
        busy={create.isPending}
        onSubmit={(name, done) => {
          create.mutate(name, {
            onSuccess: () => done(null),
            onError: (error) => done(describeError(error)),
          });
        }}
      />

      <RenameDialog key={`${renaming?.kind ?? ''}-${String(renaming?.id ?? 0)}`} target={renaming} onClose={() => setRenaming(null)} />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete tag"
        destructive
        confirmLabel="Delete tag"
        busy={remove.isPending}
        message={
          <p>
            Delete <strong>{deleting?.name}</strong>? It is removed from the{' '}
            {deleting?.postCount ?? 0} posts carrying it; nothing is unsaved.
          </p>
        }
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting === null) return;
          remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
        }}
      />
    </>
  );
}

/** Count + kebab, as the trailing hint of a checkbox row. */
function SavedRowMenu({
  label,
  count,
  onRename,
  onDelete,
}: {
  label: string;
  count: number;
  onRename: () => void;
  onDelete: () => void;
}) {
  return (
    <span className="sb-row-actions">
      <span className="faint">{count}</span>
      <Menu
        label={<KebabIcon size={13} />}
        ariaLabel={`Actions for ${label}`}
        align="right"
        triggerClassName="icon-btn icon-btn--xs"
      >
        {(close) => (
          <>
            <MenuItem
              onClick={() => {
                close();
                onRename();
              }}
            >
              Rename
            </MenuItem>
            <MenuItem
              destructive
              onClick={() => {
                close();
                onDelete();
              }}
            >
              Delete
            </MenuItem>
          </>
        )}
      </Menu>
    </span>
  );
}

/* ------------------------------------------------------------- saved date */

export type SavedDateValue = { from?: number; to?: number };

/**
 * The from/to pair over `saved_at`. Deliberately separate from the header's Range control, which
 * bounds *published* dates: "posts I saved last week" and "posts published last week" are different
 * questions and the screen lets both be asked at once.
 */
export function SavedDateSection({
  value,
  onChange,
}: {
  value: SavedDateValue;
  onChange: (next: SavedDateValue) => void;
}) {
  const set = (key: 'from' | 'to', raw: string) => {
    const next: SavedDateValue = { ...value };
    const parsed = fromDateInputValue(raw, key === 'to' ? 'end' : 'start');
    if (parsed === null) delete next[key];
    else next[key] = parsed;
    onChange(next);
  };

  return (
    <div className="sb-field">
      <div className="sb-row">
        <input
          className="input input--narrow"
          type="date"
          aria-label="Saved from"
          value={toDateInputValue(value.from)}
          onChange={(event) => set('from', event.target.value)}
        />
        <span className="muted">–</span>
        <input
          className={cx('input', 'input--narrow')}
          type="date"
          aria-label="Saved to"
          value={toDateInputValue(value.to)}
          onChange={(event) => set('to', event.target.value)}
        />
      </div>
      <p className="field__hint">When you saved it — the date range above is when it was posted.</p>
    </div>
  );
}
